/**
 * The upsell line (blocks/upsell-nudge.liquid). Every message is rendered by Liquid as a
 * <template>, one per quantity the ladder can set and purchase option; this only clones the one
 * that applies (so no HTML string is ever parsed here) and wires its button:
 *   - "Make it N" asks the ladder for that quantity (`case-ladder:set-quantity`), the one place
 *     that knows how the rungs and the selector fit together;
 *   - "Subscribe instead" asks the purchase options for the default plan.
 * A quantity the ladder cannot produce has no template, and the line hides. On a product the case
 * discount does not cover the line is delivery only and has no button (see #current for the one
 * difference that makes).
 *
 * FOCUS. Taking the step usually retires the button: "Make it 6" leads to a message with no next
 * step, so the button the shopper just pressed is hidden under their focus, which then falls to
 * <body> and a keyboard or screen reader user is thrown back to the top of the page. When that
 * happens focus moves to what the press changed: the rung now ticked (or the plan now chosen),
 * else the quantity, else Add. The new message is then read out once through the status region,
 * only after a press, so the stepper does not make it chatter.
 */
const QUANTITY_EVENT = 'quantity-selector:update';
const PURCHASE_EVENT = 'purchase-options:change';
const SET_QUANTITY_EVENT = 'case-ladder:set-quantity';
const SELECT_DEFAULT_EVENT = 'purchase-options:select-default';

class UpsellNudge extends HTMLElement {
  #plan = '';
  /** @type {'' | 'qty' | 'plan'} What the last press asked for, until the next render answers it. */
  #pressed = '';

  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.#plan =
      /** @type {HTMLInputElement | null} */ (this.section?.querySelector('purchase-options input[type="radio"]:checked'))
        ?.value ?? '';

    this.section?.addEventListener(QUANTITY_EVENT, this.#onQuantity);
    this.section?.addEventListener(PURCHASE_EVENT, this.#onPurchase);
    this.querySelector('.upsell-nudge__action')?.addEventListener('click', this.#onAction);
    this.#render();
  }

  disconnectedCallback() {
    this.section?.removeEventListener(QUANTITY_EVENT, this.#onQuantity);
    this.section?.removeEventListener(PURCHASE_EVENT, this.#onPurchase);
    this.querySelector('.upsell-nudge__action')?.removeEventListener('click', this.#onAction);
  }

  #quantity() {
    for (const input of this.section?.querySelectorAll('quantity-selector-component input[name="quantity"]') ?? []) {
      if (!input.closest('product-card, cart-items-component')) {
        return parseInt(/** @type {HTMLInputElement} */ (input).value, 10) || 1;
      }
    }
    return 1;
  }

  /**
   * The message for this quantity and plan. Failing an exact one, a message marked
   * `data-and-above` for the same plan at a lower quantity: the delivery-only line marks its
   * "delivery is free" this way, because on a product with no case ladder the stepper can go past
   * every quantity Liquid rendered. Wine's messages never carry the mark.
   * @returns {HTMLTemplateElement | null}
   */
  #current() {
    return this.#find(this.#quantity(), this.#plan);
  }

  /**
   * The message for a quantity and plan, by the rule #current describes.
   * @param {number} quantity
   * @param {string} plan
   * @returns {HTMLTemplateElement | null}
   */
  #find(quantity, plan) {
    const key = `${quantity}|${plan}`;
    /** @type {HTMLTemplateElement | null} */
    let open = null;
    for (const node of this.querySelectorAll('template[data-key]')) {
      const template = /** @type {HTMLTemplateElement} */ (node);
      if (template.dataset.key === key) return template;
      if (template.hasAttribute('data-and-above')) {
        const [from, templatePlan] = (template.dataset.key ?? '').split('|');
        if (templatePlan === plan && Number(from) <= quantity) open = template;
      }
    }
    return open;
  }

  #render = () => {
    const action = /** @type {HTMLButtonElement | null} */ (this.querySelector('.upsell-nudge__action'));
    const hadFocus = !!action && document.activeElement === action;
    const pressed = this.#pressed;
    this.#pressed = '';

    const message = this.#current();
    const reserved = this.#reserve();
    if (!message) {
      // Another purchase option has a message at this quantity: keep its room, show nothing.
      if (reserved) {
        for (const icon of this.querySelectorAll('.upsell-nudge__icon')) /** @type {HTMLElement} */ (icon).hidden = true;
        this.querySelector('.upsell-nudge__message')?.replaceChildren();
        if (action) action.hidden = true;
        this.hidden = false;
      } else {
        this.hidden = true;
      }
      if (hadFocus) this.#handOff(pressed);
      return;
    }
    for (const icon of this.querySelectorAll('.upsell-nudge__icon')) {
      /** @type {HTMLElement} */ (icon).hidden = /** @type {HTMLElement} */ (icon).dataset.kind !== message.dataset.kind;
    }
    const text = this.querySelector('.upsell-nudge__message');
    text?.replaceChildren(message.content.cloneNode(true));
    if (action) {
      action.textContent = message.dataset.action ?? '';
      action.hidden = !message.dataset.action;
    }
    this.hidden = false;
    if (hadFocus && action?.hidden) this.#handOff(pressed);
    if (pressed) this.#announce(text?.textContent?.trim() ?? '');
  };

  /**
   * Holds the line at the height of the longest message any purchase option would show at this
   * quantity (2026-10-15). The message changes with the option (a case step on One-off, the
   * delivery gap on a plan), and when it wrapped differently the Add button under it moved by a
   * line on every change of pill. The other options' messages are laid in the same grid cell,
   * invisible and out of the accessibility tree, so the cell takes the tallest. A change of
   * QUANTITY can still change the height: that is the ladder or the stepper, which the shopper is
   * already looking at. Returns whether any other option has a message here.
   * @returns {boolean}
   */
  #reserve() {
    for (const ghost of this.querySelectorAll('.upsell-nudge__ghost')) ghost.remove();
    const quantity = this.#quantity();
    let any = false;
    for (const radio of this.section?.querySelectorAll('purchase-options input[type="radio"]') ?? []) {
      const plan = /** @type {HTMLInputElement} */ (radio).value;
      if (plan === this.#plan) continue;
      const template = this.#find(quantity, plan);
      if (!template) continue;
      any = true;
      const ghost = document.createElement('p');
      ghost.className = 'upsell-nudge__text upsell-nudge__ghost';
      ghost.setAttribute('aria-hidden', 'true');
      const words = document.createElement('span');
      words.className = 'upsell-nudge__message';
      words.append(template.content.cloneNode(true));
      ghost.append(words);
      if (template.dataset.action) {
        const label = document.createElement('span');
        label.className = 'upsell-nudge__action';
        label.textContent = template.dataset.action;
        ghost.append(' ', label);
      }
      this.append(ghost);
    }
    return any;
  }

  /**
   * Moves focus off a button that has just been hidden, to the control the press changed.
   * @param {'' | 'qty' | 'plan'} pressed
   */
  #handOff(pressed) {
    const section = this.section;
    if (!section) return;
    const visible = (/** @type {Element | null} */ el) =>
      el instanceof HTMLElement && !el.hidden && el.getClientRects().length > 0 ? el : null;
    const candidates =
      pressed === 'plan'
        ? ['purchase-options input[type="radio"]:checked']
        : ['case-ladder input[type="radio"]:checked'];
    candidates.push('quantity-selector-component input[name="quantity"]', '[ref="addToCartButton"]');
    for (const selector of candidates) {
      for (const el of section.querySelectorAll(selector)) {
        if (el.closest('product-card, cart-items-component')) continue;
        const target = visible(el);
        if (target) {
          target.focus();
          return;
        }
      }
    }
  }

  /** @param {string} text */
  #announce(text) {
    const status = this.querySelector('.upsell-nudge__status');
    if (!status || !text) return;
    status.textContent = '';
    // A changed node is what a live region reads; clearing first makes a repeat message count.
    requestAnimationFrame(() => {
      status.textContent = text;
    });
  }

  #onQuantity = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card, cart-items-component')) return;
    // Some selector paths dispatch before the input settles; read on the next frame.
    requestAnimationFrame(this.#render);
  };

  #onPurchase = (event) => {
    this.#plan = event.detail?.plan ?? '';
    this.#render();
  };

  #onAction = () => {
    const message = this.#current();
    if (!message) return;
    if (message.hasAttribute('data-plan')) {
      this.#pressed = 'plan';
      this.section?.dispatchEvent(new CustomEvent(SELECT_DEFAULT_EVENT));
      return;
    }
    const quantity = Number(message.dataset.qty);
    if (quantity > 0) {
      this.#pressed = 'qty';
      this.section?.dispatchEvent(new CustomEvent(SET_QUANTITY_EVENT, { detail: { quantity } }));
    }
  };
}

if (!customElements.get('upsell-nudge')) customElements.define('upsell-nudge', UpsellNudge);
